/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useRef } from "react";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { useToggle } from "@adapptio/lib-components/src/Hooks/useToggle";
import { useBodyClick } from "../../hooks/useBodyClick";
import {
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE,
	FieldFloatingPanel
} from "./FieldFloatingPanel";
import { FC_ANCHOR, FC_DIRECTION, FC_SIZE } from "../FloatingContainer";
import { FieldText } from "./FieldText";
import { FieldLabel } from "./FieldLabel";
import { Icon, ICON_SIZE } from "../Icon";
import { FieldValidationErrors } from "./FieldValidationErrors";

export interface IFieldSecretProps {
	secretKey: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	grow?: boolean;
	onChange?: (secretKey: string) => void;
}

export const FieldSecret: FC<IFieldSecretProps> = ({
	secretKey,
	readOnly,
	validationErrors,
	className,
	grow,
	onChange
}) => {
	const t = useTranslate();
	const elRef = useRef();
	const [ configOpen, toggleConfig, openConfig, closeConfig ] = useToggle();

	const baseClass = BEM("edt-field", "selector", {
		"config-open": configOpen
	});
	const configClass = BEM("edt-field--type-secret", "config");

	const handleOpenConfigClick = useCallback<React.MouseEventHandler<HTMLElement>>(
		(event) => {
			if (readOnly) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			openConfig();
		},
		[ openConfig, readOnly ]
	);

	useBodyClick(() => {
		if (configOpen) {
			closeConfig();
		}
	}, configOpen);

	return (
		<FieldWrapper
			type="secret"
			className={className}
			grow={grow}
			wrapperRef={elRef}
			onClick={handleOpenConfigClick}
		>
			<button
				className={baseClass.toClassName()}
				onClick={toggleConfig}
				ref={elRef}
				disabled={readOnly}
			>
				<span className={baseClass.element("selector-label").toClassName()}>
					{secretKey
						? t("editor", "Secret:") + " " + secretKey
						: t("editor", "Click to configure secret")}
				</span>
				<FieldValidationErrors errors={validationErrors} />
				<Icon name="mdi/lock" size={ICON_SIZE.CUSTOM} />
			</button>
			<FieldFloatingPanel
				for={elRef}
				isOpened={configOpen}
				anchor={FC_ANCHOR.CENTER_BOTTOM}
				direction={FC_DIRECTION.HR}
				size={FC_SIZE.FIT_MIN}
				style={FIELD_FLOATING_PANEL_STYLE.STICKY}
				schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
				onClose={closeConfig}
			>
				<div className={configClass.element("config-field").toClassName()}>
					<FieldLabel>{t("editor", "Secret value:")}</FieldLabel>
					<FieldText
						value={secretKey}
						onChange={onChange}
						type="password"
						autoFocus
					/>
				</div>
			</FieldFloatingPanel>
		</FieldWrapper>
	);
};
